import type {
  VectorEntry,
  VectorId,
  VectorSearchResult,
} from '@neureus/vector-db';
import type { RAGEnv } from '../types';

// Local VectorSearchOptions interface
interface VectorSearchOptions {
  topK?: number;
  minSimilarity?: number;
  includeValues?: boolean;
  filter?: Record<string, any>;
  hybridWeight?: number;
}

/**
 * Cloudflare Vectorize storage implementation
 * Provides native vector storage with high performance and global distribution
 */
export class VectorizeStorage {
  private env: RAGEnv;
  private vectorize: any; // Cloudflare Vectorize index
  private namespace: string;

  constructor(env: RAGEnv, namespace: string = 'default') {
    this.env = env;
    this.vectorize = env.VECTORIZE;
    this.namespace = namespace;

    if (!this.vectorize) {
      throw new Error('Cloudflare Vectorize binding not available');
    }
  }

  /**
   * Initialize vectorize index
   */
  async initialize(): Promise<void> {
    try {
      // Vectorize index should be pre-configured via wrangler.toml
      // This method can be used for runtime validation
      const info = await this.vectorize.describe();
      console.log(`Vectorize index initialized: ${info.name}, dimensions: ${info.dimensions}`);
    } catch (error) {
      console.warn('Vectorize initialization check failed:', error);
      // Don't throw here as the index might still work
    }
  }

  /**
   * Insert vectors into Vectorize
   */
  async insertVectors(entries: VectorEntry[]): Promise<void> {
    if (entries.length === 0) return;

    try {
      const vectors = entries.map(entry => ({
        id: this.createNamespacedId(entry.id),
        values: entry.vector,
        metadata: {
          ...entry.metadata,
          namespace: this.namespace,
          originalId: entry.id,
          timestamp: entry.timestamp || Date.now(),
        },
      }));

      const result = await this.vectorize.insert(vectors);
      
      if (result.count !== vectors.length) {
        console.warn(`Expected to insert ${vectors.length} vectors, but ${result.count} were inserted`);
      }
    } catch (error) {
      throw new Error(`Vectorize insert failed: ${error}`);
    }
  }

  /**
   * Upsert vectors (insert or update)
   */
  async upsertVectors(entries: VectorEntry[]): Promise<void> {
    if (entries.length === 0) return;

    try {
      const vectors = entries.map(entry => ({
        id: this.createNamespacedId(entry.id),
        values: entry.vector,
        metadata: {
          ...entry.metadata,
          namespace: this.namespace,
          originalId: entry.id,
          timestamp: entry.timestamp || Date.now(),
        },
      }));

      const result = await this.vectorize.upsert(vectors);
      
      if (result.count !== vectors.length) {
        console.warn(`Expected to upsert ${vectors.length} vectors, but ${result.count} were processed`);
      }
    } catch (error) {
      throw new Error(`Vectorize upsert failed: ${error}`);
    }
  }

  /**
   * Search for similar vectors
   */
  async searchSimilar(
    queryVector: number[],
    options: VectorSearchOptions = {}
  ): Promise<VectorSearchResult[]> {
    try {
      const queryOptions = {
        topK: options.topK || 10,
        includeValues: options.includeValues ?? false,
        includeMetadata: true,
        filter: this.buildFilter(options.filter),
      };

      const result = await this.vectorize.query(queryVector, queryOptions);

      return result.matches
        .filter((match: any) => 
          !match.metadata?.namespace || match.metadata.namespace === this.namespace
        )
        .map((match: any) => ({
          id: match.metadata?.originalId || match.id,
          score: match.score,
          vector: match.values || [],
          metadata: this.cleanMetadata(match.metadata),
        }))
        .filter((result: VectorSearchResult) => 
          !options.minSimilarity || result.score >= options.minSimilarity
        );
    } catch (error) {
      throw new Error(`Vectorize search failed: ${error}`);
    }
  }

  /**
   * Get vector by ID
   */
  async getVector(id: VectorId): Promise<VectorEntry | null> {
    try {
      // Vectorize doesn't have a direct get method, so we'll use a search with exact ID
      const result = await this.vectorize.query(
        new Array(768).fill(0), // Dummy query vector
        {
          topK: 1,
          includeValues: true,
          includeMetadata: true,
          filter: { originalId: id, namespace: this.namespace },
        }
      );

      if (result.matches.length === 0) {
        return null;
      }

      const match = result.matches[0];
      return {
        id: match.metadata?.originalId || match.id,
        vector: match.values || [],
        metadata: this.cleanMetadata(match.metadata),
        timestamp: match.metadata?.timestamp,
      };
    } catch (error) {
      console.warn(`Failed to get vector ${id}:`, error);
      return null;
    }
  }

  /**
   * Delete vectors by IDs
   */
  async deleteVectors(ids: VectorId[]): Promise<boolean> {
    if (ids.length === 0) return true;

    try {
      const namespacedIds = ids.map(id => this.createNamespacedId(id));
      const result = await this.vectorize.deleteByIds(namespacedIds);
      
      return result.count > 0;
    } catch (error) {
      console.warn('Vectorize delete failed:', error);
      return false;
    }
  }

  /**
   * Get index information and statistics
   */
  async getIndexInfo(): Promise<{
    name: string;
    dimensions: number;
    vectorCount: number;
    metric: string;
  }> {
    try {
      const info = await this.vectorize.describe();
      return {
        name: info.name,
        dimensions: info.dimensions,
        vectorCount: info.vectorCount || 0,
        metric: info.metric || 'cosine',
      };
    } catch (error) {
      throw new Error(`Failed to get index info: ${error}`);
    }
  }

  /**
   * Batch operations for better performance
   */
  async batchOperations(operations: {
    insert?: VectorEntry[];
    update?: VectorEntry[];
    delete?: VectorId[];
  }): Promise<void> {
    const promises: Promise<any>[] = [];

    if (operations.insert && operations.insert.length > 0) {
      promises.push(this.insertVectors(operations.insert));
    }

    if (operations.update && operations.update.length > 0) {
      promises.push(this.upsertVectors(operations.update));
    }

    if (operations.delete && operations.delete.length > 0) {
      promises.push(this.deleteVectors(operations.delete));
    }

    await Promise.all(promises);
  }

  /**
   * Hybrid search combining vector similarity and metadata filtering
   */
  async hybridSearch(
    queryVector: number[],
    textQuery?: string,
    options: VectorSearchOptions = {}
  ): Promise<VectorSearchResult[]> {
    try {
      // Start with vector similarity search
      const vectorResults = await this.searchSimilar(queryVector, {
        ...options,
        topK: (options.topK || 10) * 2, // Get more results for reranking
      });

      // If no text query, return vector results
      if (!textQuery) {
        return vectorResults.slice(0, options.topK || 10);
      }

      // Combine with text-based filtering/ranking
      const textFilteredResults = vectorResults.filter(result => {
        if (!result.metadata?.content) return true;
        
        // Simple text matching (in production, use proper text search)
        const content = result.metadata.content.toLowerCase();
        const query = textQuery.toLowerCase();
        
        return content.includes(query) || 
               this.calculateTextSimilarity(content, query) > 0.3;
      });

      // Rerank results based on combined vector + text similarity
      const rerankedResults = textFilteredResults.map(result => ({
        ...result,
        score: this.combineScores(
          result.score,
          this.calculateTextSimilarity(result.metadata?.content || '', textQuery),
          options.hybridWeight || 0.7
        ),
      }));

      // Sort by combined score and return top results
      rerankedResults.sort((a, b) => b.score - a.score);
      return rerankedResults.slice(0, options.topK || 10);
    } catch (error) {
      throw new Error(`Hybrid search failed: ${error}`);
    }
  }

  /**
   * Create namespaced ID to avoid conflicts
   */
  private createNamespacedId(id: VectorId): string {
    return `${this.namespace}:${id}`;
  }

  /**
   * Build filter for Vectorize query
   */
  private buildFilter(filter?: Record<string, any>): Record<string, any> | undefined {
    if (!filter) {
      return { namespace: this.namespace };
    }

    return {
      ...filter,
      namespace: this.namespace,
    };
  }

  /**
   * Clean metadata by removing internal fields
   */
  private cleanMetadata(metadata: any): Record<string, any> | undefined {
    if (!metadata) return undefined;

    const cleaned = { ...metadata };
    delete cleaned.namespace;
    delete cleaned.originalId;
    delete cleaned.timestamp;

    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  /**
   * Calculate simple text similarity (placeholder)
   */
  private calculateTextSimilarity(text1: string, text2: string): number {
    // Simple Jaccard similarity
    const words1 = new Set(text1.toLowerCase().split(/\s+/));
    const words2 = new Set(text2.toLowerCase().split(/\s+/));
    
    const intersection = new Set([...words1].filter(word => words2.has(word)));
    const union = new Set([...words1, ...words2]);
    
    return intersection.size / union.size;
  }

  /**
   * Combine vector and text similarity scores
   */
  private combineScores(
    vectorScore: number,
    textScore: number,
    hybridWeight: number
  ): number {
    return vectorScore * hybridWeight + textScore * (1 - hybridWeight);
  }

  /**
   * Create multiple namespaces for different use cases
   */
  async createNamespace(namespace: string): Promise<VectorizeStorage> {
    return new VectorizeStorage(this.env, namespace);
  }

  /**
   * Clear all vectors in the current namespace
   */
  async clearNamespace(): Promise<void> {
    try {
      // Get all vectors in this namespace (this is inefficient but Vectorize doesn't have namespace clear)
      const allVectors = await this.listAllVectors();
      const ids = allVectors.map(v => v.id);
      
      if (ids.length > 0) {
        await this.deleteVectors(ids);
      }
    } catch (error) {
      console.warn('Failed to clear namespace:', error);
    }
  }

  /**
   * List all vectors in namespace (for management purposes)
   */
  private async listAllVectors(): Promise<VectorEntry[]> {
    // This is a workaround since Vectorize doesn't have a list method
    // In practice, you'd maintain an index of vector IDs
    console.warn('listAllVectors is a placeholder - implement proper vector ID tracking');
    return [];
  }

  /**
   * Performance monitoring
   */
  async getPerformanceMetrics(): Promise<{
    avgQueryLatency: number;
    indexUtilization: number;
    errorRate: number;
  }> {
    // These would be tracked by the application
    return {
      avgQueryLatency: 0,
      indexUtilization: 0,
      errorRate: 0,
    };
  }
}

/**
 * Factory function to create VectorizeStorage with error handling
 */
export function createVectorizeStorage(env: RAGEnv, namespace?: string): VectorizeStorage | null {
  try {
    if (!env.VECTORIZE) {
      console.warn('Vectorize not available, falling back to alternative storage');
      return null;
    }
    return new VectorizeStorage(env, namespace);
  } catch (error) {
    console.warn('Failed to create VectorizeStorage:', error);
    return null;
  }
}